import { useCallback, useEffect, useState } from 'react';

import {
  fetchGiftPasses,
  type GiftPassListRow,
  type GiftPassSortOrder,
  type GiftPassSourceFilter,
  type GiftPassStatusFilter,
  type GiftPassSummary,
} from '../../api/gift-passes';
import { usePagination } from '../../hooks/usePagination';

export const PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_SUMMARY: GiftPassSummary = {
  totalPasses: 0,
  byStatus: { ISSUED: 0, SENT: 0, REDEEMED: 0, VOIDED: 0, EXPIRED: 0 },
};

/** The query controls. Every change also returns the list to page 1 in the same render. */
function useGiftPassQuery(resetPage: () => void) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatusState] = useState<GiftPassStatusFilter>('all');
  const [source, setSourceState] = useState<GiftPassSourceFilter>('all');
  const [order, setOrderState] = useState<GiftPassSortOrder>('desc');

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      resetPage();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, resetPage]);

  const withReset =
    <T>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      resetPage();
    };

  const clear = () => {
    setSearchInput('');
    setSearch('');
    setStatusState('all');
    setSourceState('all');
    resetPage();
  };

  return {
    searchInput,
    setSearchInput,
    search,
    status,
    setStatus: withReset(setStatusState),
    source,
    setSource: withReset(setSourceState),
    order,
    setOrder: withReset(setOrderState),
    clear,
  };
}

export function useGiftPassListState() {
  const [rows, setRows] = useState<GiftPassListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<GiftPassSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pager = usePagination(total, PAGE_SIZE);
  const { setOffset } = pager;
  const resetPage = useCallback(() => setOffset(0), [setOffset]);
  const query = useGiftPassQuery(resetPage);
  const { search, status, source, order } = query;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    fetchGiftPasses(
      { search, status, source, order, limit: PAGE_SIZE, offset: pager.offset },
      signal,
    )
      .then((res) => {
        if (signal.aborted) return;
        setRows(res.giftPasses);
        setTotal(res.pagination.total);
        setSummary(res.summary);
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load gift passes');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [search, status, source, order, pager.offset]);

  return { ...query, rows, total, summary, loading, error, pager };
}
